'use client';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import FullScreenLoading from '@/components/FullScreenLoading';
import { authRoutes } from '@/consts/route';
import { useReferralCodeByLink } from '@/hooks/useReferral';
import {
  useSessionReferral,
  useSessionReferralBrandName,
  useSessionReferralDirect,
} from '@/hooks/useSessionReferral';

const ReferralClientRedirect = ({ referral }: { referral: string }) => {
  const { mutateAsync: checkCodeByLink, isSuccess, isError, data } = useReferralCodeByLink();
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeoutReached, setTimeoutReached] = useState(false);

  const [, setReferral] = useSessionReferral();
  const [, setIsDirect] = useSessionReferralDirect();
  const [, setBrandName] = useSessionReferralBrandName();

  const router = useRouter();

  // 무한로딩 방지를 위한 타임아웃 (10초)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isSuccess && !isError && isProcessing) {
        setTimeoutReached(true);
        router.push(authRoutes.signUp);
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [isProcessing, isSuccess, isError, router]);

  useEffect(() => {
    if (!referral) {
      console.warn('No referral code provided, redirecting to home');
      return router.push('/');
    }

    if (isProcessing || timeoutReached) return; // 중복 실행 방지

    setIsProcessing(true);

    checkCodeByLink(referral).catch((err) => {
      console.error('Referral code check failed:', err);
      setIsProcessing(false);
      // 에러 발생 시 일정 시간 후 회원가입 페이지로 이동
      setTimeout(() => {
        router.push('/');
      }, 1000);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referral, timeoutReached]);

  useEffect(() => {
    if ((!isSuccess && !isError) || timeoutReached) return;

    setIsProcessing(false); // 처리 완료 상태로 변경

    if (isSuccess && data) {
      setReferral(data?.referral_code);
      if (data.is_simple_signup) {
        setIsDirect(true);
      }

      if (data.brand?.display_name) {
        setBrandName(data.brand.display_name);
      }

      // 기존 사용자인지 확인
      if (data?.is_signup_redirect) {
        router.push(authRoutes.signUp);
        return;
      }
    }

    router.push('/');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data, isError, timeoutReached]);
  return <FullScreenLoading />;
};

export default ReferralClientRedirect;
